import { useEffect, useMemo, useRef, useState } from 'react'
import { Crown, Gamepad2, Headphones, Radio, Trophy, Tv } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { gatewayClient } from '@/lib/gatewaySingleton'
import { roleColor } from '@/lib/permissions'
import { memberName } from '@/lib/guildUi'
import { ChannelType, type ListMember, type MemberListItem } from '@/types/api'
import { cx } from '@/lib/cx'
import { Avatar } from './Avatar'
import { memberAvatarUrl } from '@/lib/avatar'
import { activityOf, customStatusOf, isOnline } from '@/lib/presence'
import { userCardHandler, userContextHandler } from '@/store/contextMenu'
import styles from './MemberList.module.css'
import { StyledName } from './StyledName'
import { ServerTag } from './ServerTag'
import { plateUrl } from '@/lib/decor'

const ROW = 44 // высота строки (заголовок группы и участник одинаковы, чтобы индекс считался арифметикой)
const BLOCK = 100 // сервер отдаёт диапазоны по 100 строк
const OVERSCAN = 6
const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/**
 * Ленивый список участников (op 14): сервер присылает только запрошенные диапазоны плоского списка
 * «заголовок группы + участники», клиент рисует видимое окно и просит недостающие блоки при прокрутке.
 */
export function MemberList({ guildId }: { guildId: string }) {
  const view = useAppStore((s) => s.view)
  const gatewayStatus = useAppStore((s) => s.gateway)
  const guild = useGuildStore((s) => s.guilds[guildId])
  const channels = useGuildStore((s) => s.channels[guildId])
  const list = useGuildStore((s) => s.memberLists[guildId])
  const loadMembers = useGuildStore((s) => s.loadMembers)
  const [scroll, setScroll] = useState({ top: 0, height: 640 })
  const requested = useRef('')
  const ref = useRef<HTMLElement | null>(null)

  /*
   * У ветки список родителя. Канал не открыт — на сервере одни голосовые каналы или их нет вовсе —
   * просим список по id самого сервера: сервер отдаёт всех участников. Раньше подписки тогда не было
   * совсем, и «Загружаем участников…» висело, пока не зайдёшь в голосовой канал.
   */
  const channelId = useMemo(() => {
    const id = view.kind === 'guild' ? view.channelId : null
    if (!id) return guildId
    const ch = channels?.[id]
    return ch?.type === ChannelType.PublicThread && ch.parent_id ? ch.parent_id : id
  }, [view, channels, guildId])

  // участники для остальных частей интерфейса (упоминания, авторы) по-прежнему подтягиваются REST-ом
  useEffect(() => {
    void loadMembers(guildId)
  }, [guildId, loadMembers])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(() => setScroll((s) => (s.height === el.clientHeight ? s : { ...s, height: el.clientHeight })))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const total = list ? list.groups.reduce((n, g) => n + 1 + g.count, 0) : 0
  const first = Math.max(0, Math.floor(scroll.top / ROW) - OVERSCAN)
  const last = Math.min(Math.max(total - 1, 0), Math.ceil((scroll.top + scroll.height) / ROW) + OVERSCAN)

  // после переподключения подписка теряется — просим заново
  useEffect(() => {
    requested.current = ''
  }, [gatewayStatus])

  // подписка на видимые блоки (и всегда на первый): смена канала, прокрутка, новое соединение
  useEffect(() => {
    if (gatewayStatus !== 'ready') return
    const blocks = new Set<number>([0])
    for (let b = Math.floor(first / BLOCK); b <= Math.floor(last / BLOCK); b++) blocks.add(b)
    const ranges = [...blocks].sort((a, b) => a - b).slice(0, 3).map((b) => [b * BLOCK, b * BLOCK + BLOCK - 1] as [number, number])
    const key = `${channelId}|${ranges.map((r) => r[0]).join(',')}`
    if (requested.current === key) return
    requested.current = key
    gatewayClient()?.subscribeMemberList(guildId, channelId, ranges)
  }, [guildId, channelId, gatewayStatus, first, last])

  if (!guild) return null

  const groupLabel = (id: string) => (id === 'online' ? 'В сети' : id === 'offline' ? 'Не в сети' : (guild.roles.find((r) => r.id === id)?.name ?? 'Роль'))
  const rows: React.ReactNode[] = []
  for (let i = first; i <= last && list; i++) {
    const it: MemberListItem | undefined = list.items[i]
    rows.push(
      <div key={i} className={styles.row} style={{ top: i * ROW }}>
        {it?.group ? (
          <h3 className={styles.group}>{groupLabel(it.group.id)} — {it.group.count}</h3>
        ) : it?.member ? (
          <MemberRow guildId={guildId} m={it.member} ownerId={guild.owner_id} roles={guild.roles} />
        ) : (
          <div className={styles.skeleton} aria-hidden="true" />
        )}
      </div>,
    )
  }

  return (
    <aside
      ref={ref}
      className={cx(styles.list, 'scroller')}
      aria-label="Участники"
      onScroll={(e) => setScroll({ top: e.currentTarget.scrollTop, height: e.currentTarget.clientHeight })}
    >
      <div className={styles.wco} aria-hidden="true" />
      {list ? <div className={styles.virtual} style={{ height: total * ROW }}>{rows}</div> : <p className={styles.group}>Загружаем участников…</p>}
    </aside>
  )
}

/** Значок рядом с тем, чем человек занят: игра, трансляция, музыка, видео, соревнование. */
const ACTIVITY_ICON: Record<number, typeof Gamepad2> = { 0: Gamepad2, 1: Radio, 2: Headphones, 3: Tv, 5: Trophy }

function MemberRow({ m, ownerId, roles, guildId }: { m: ListMember; ownerId: string; roles: { id: string; position: number; color: number }[]; guildId: string }) {
  const name = memberName(m)
  const color = roleColor(roles as Parameters<typeof roleColor>[0], m.roles)
  const status = m.presence.status
  const offline = !isOnline(status)
  // второй строкой под именем, как в Discord: свой статус, иначе чем занят
  const custom = offline ? '' : customStatusOf(m.presence.activities)
  const activity = offline || custom ? null : activityOf(m.presence.activities)
  const ActivityIcon = activity ? (ACTIVITY_ICON[activity.type] ?? Gamepad2) : null

  const timedOut = Boolean(m.communication_disabled_until && new Date(m.communication_disabled_until) > new Date())
  return (
    <button type="button" className={styles.member} data-offline={offline ? '' : undefined} data-tooltip={`@${m.user.username}`} onContextMenu={userContextHandler(m.user.id, { user: m.user, guildId })} onClick={userCardHandler(m.user.id, { user: m.user, guildId })}>
      {m.user.collectibles?.nameplate ? <span className={styles.plate} style={{ backgroundImage: `url(${plateUrl(m.user.collectibles.nameplate.asset)})` }} aria-hidden="true" /> : null}
      <Avatar initial={name.slice(0, 1).toUpperCase()} color={colorOf(m.user.id)} size={32} status={offline ? 'offline' : status} src={memberAvatarUrl(guildId, m)} decoration={m.user.avatar_decoration_data} />
      <span className={styles.text}>
        <span className={styles.name} style={color ? { color } : undefined}>
          <StyledName name={name} style={m.user.display_name_styles} className={styles.nameText} />
          {m.user.primary_guild ? <ServerTag tag={m.user.primary_guild.tag} color={m.user.primary_guild.color} className={styles.serverTag} /> : null}
          {m.user.bot ? <span className={styles.botTag}>БОТ</span> : null}
        </span>
        {timedOut ? (
          <span className={styles.activity}><span className={styles.activityText}>В таймауте</span></span>
        ) : custom ? (
          <span className={styles.activity} data-tooltip={custom}><span className={styles.activityText}>{custom}</span></span>
        ) : activity && ActivityIcon ? (
          // игра — одним названием со значком геймпада; остальное — с глаголом: «Слушает радио»
          <span className={styles.activity} data-tooltip={activity.label}>
            <ActivityIcon size={12} className={styles.activityIcon} />
            <span className={styles.activityText}>{activity.type === 0 ? activity.name : activity.label}</span>
          </span>
        ) : null}
      </span>
      {m.user.id === ownerId ? <Crown size={14} className={styles.crown} aria-label="Владелец сервера" /> : null}
    </button>
  )
}
